import { useState, useCallback } from "react"
import {
  Survey,
  DataTopicStatus,
  EditDataTopicsRequest,
  editSurveyDataTopics,
} from "@/services/surveysListService"
import { useAuth } from "@/hooks/useAuth"

/** The data topic field names and their display labels. */
export const DATA_TOPIC_FIELDS: { key: keyof EditDataTopicsRequest; label: string }[] = [
  { key: "feverTreatmentSeeking", label: "Fever Treatment Seeking" },
  { key: "antimalarials", label: "Antimalarials" },
  { key: "chemoprevention", label: "Chemoprevention" },
  { key: "itn", label: "ITN" },
  { key: "irs", label: "IRS" },
  { key: "improvedHousing", label: "Improved Housing" },
  { key: "rdt", label: "RDT" },
  { key: "microscopy", label: "Microscopy" },
]

/** All selectable values for a data topic dropdown. */
export const DATA_TOPIC_OPTIONS: DataTopicStatus[] = [
  DataTopicStatus.MAY_CONTAIN,
  DataTopicStatus.DOES_NOT_CONTAIN,
  DataTopicStatus.DOES_CONTAIN,
  DataTopicStatus.MICRO_DATA_EXTRACTED,
  DataTopicStatus.REPORT_DATA_EXTRACTED,
]

/**
 * Build the initial form values for a survey from its current data topic fields.
 *
 * @param survey - The survey whose current values should be used.
 * @returns A record mapping each field key to its current DataTopicStatus string.
 */
export function buildInitialTopicValues(survey: Survey): Record<string, DataTopicStatus> {
  const values: Record<string, DataTopicStatus> = {}
  for (const field of DATA_TOPIC_FIELDS) {
    const current = survey[field.key as keyof Survey]
    values[field.key] =
      current && DATA_TOPIC_OPTIONS.includes(current as DataTopicStatus)
        ? (current as DataTopicStatus)
        : DATA_TOPIC_OPTIONS[0]
  }
  return values
}

/**
 * Hook that manages the inline data-topic editing state for a survey table.
 *
 * Tracks which survey row is being edited, the current form values for each
 * topic field, and submission status. Exposes handlers for opening/closing the
 * inline editor, changing a field value, and saving the changes.
 *
 * @param onUpdated - Callback invoked after a successful save so the parent
 *   can refresh the surveys list.
 * @returns An object with the current editing state and action handlers.
 */
export function useInlineDataTopicEdit(onUpdated: () => void): {
  editingRowKey: string | null
  formValues: Record<string, DataTopicStatus>
  submitting: boolean
  submitError: string | null
  openEdit: (survey: Survey, rowKey: string) => void
  closeEdit: () => void
  handleFieldChange: (fieldKey: string, value: DataTopicStatus) => void
  handleSave: (survey: Survey) => Promise<void>
} {
  const { getAccessToken } = useAuth()

  /** The row key of the survey currently in edit mode, or null. */
  const [editingRowKey, setEditingRowKey] = useState<string | null>(null)

  /** Current form values while a row is being edited. */
  const [formValues, setFormValues] = useState<Record<string, DataTopicStatus>>({})

  const [submitting, setSubmitting] = useState<boolean>(false)
  const [submitError, setSubmitError] = useState<string | null>(null)

  /**
   * Open the inline editor for a survey row, seeding form values from the
   * survey's current data topic field values.
   *
   * @param survey - The survey whose data topics are being edited.
   * @param rowKey - The stable React row key for this survey row.
   */
  const openEdit = useCallback((survey: Survey, rowKey: string): void => {
    setFormValues(buildInitialTopicValues(survey))
    setSubmitError(null)
    setEditingRowKey(rowKey)
  }, [])

  /**
   * Close the inline editor without saving.
   */
  const closeEdit = useCallback((): void => {
    setEditingRowKey(null)
    setSubmitError(null)
  }, [])

  /**
   * Update a single field value in the current form state.
   *
   * @param fieldKey - The data topic field key to update.
   * @param value - The new DataTopicStatus value to set.
   */
  const handleFieldChange = useCallback(
    (fieldKey: string, value: DataTopicStatus): void => {
      setFormValues(prev => ({ ...prev, [fieldKey]: value }))
    },
    []
  )

  /**
   * Submit the edited data topic values for the given survey.
   *
   * Only fields whose value has actually changed from the survey's current
   * state are sent. Closes the editor on success and calls onUpdated.
   *
   * @param survey - The survey being saved.
   */
  const handleSave = useCallback(
    async (survey: Survey): Promise<void> => {
      setSubmitError(null)

      const initial = buildInitialTopicValues(survey)
      const changedFields: Record<string, string> = {}
      for (const field of DATA_TOPIC_FIELDS) {
        if (formValues[field.key] !== initial[field.key]) {
          changedFields[field.key] = formValues[field.key]
        }
      }

      // Nothing changed — just close.
      if (Object.keys(changedFields).length === 0) {
        setEditingRowKey(null)
        return
      }

      const request: EditDataTopicsRequest = {
        mapSurveyId: survey.mapSurveyId,
        ...changedFields,
      } as EditDataTopicsRequest

      setSubmitting(true)
      try {
        const token = await getAccessToken()
        await editSurveyDataTopics(token, request)
        setEditingRowKey(null)
        onUpdated()
      } catch (err) {
        setSubmitError(err instanceof Error ? err.message : "Failed to update data topics")
      } finally {
        setSubmitting(false)
      }
    },
    [formValues, getAccessToken, onUpdated]
  )

  return {
    editingRowKey,
    formValues,
    submitting,
    submitError,
    openEdit,
    closeEdit,
    handleFieldChange,
    handleSave,
  }
}
